
import { Component, Vue, Watch } from 'vue-property-decorator';
import CreateWorkspace, { ComponentParameter_Post } from "../models/CreateWorkspace";
import { Template, TemplateParameter } from '../models/Template';
import { validationMixin } from 'vuelidate';
import { required, minLength, alphaNum, maxLength, numeric } from 'vuelidate/lib/validators';
import PeopleSearch from '../components/PeopleSearch.vue';
import VatWarning from '../components/shared/VatWarning.vue';
import { Workspace } from '../models/Workspace';
import Datepicker from 'vuejs-datepicker';
import { mapState } from 'vuex';
import { Dictionary } from 'vue-router/types/router';
import { AddWorkspacesParams, GetWorkspacesParams } from '@/models/StoreClasses'
import { Helper } from '@/models/HelperClasses';
import { errorToaster } from '@/data/ajax';
import { SizeAndPrice, VmImage } from '@/models/VirtualMachine';
import SizeAndPriceTable from '../components/shared/SizeAndPriceTable.vue';
import { VreConfiguration } from '@/models/VreConfiguration';

Component.registerHooks(["validations"])


@Component({
  mixins: [validationMixin],
  components: { PeopleSearch, Datepicker, SizeAndPriceTable, VatWarning },
  computed: mapState(["workspaces", "templates", "sizeAndPrices", "vmImages", "vreConfiguration"])
})


export default class WorkspaceCreate extends Vue {
  createWorkspace: CreateWorkspace = new CreateWorkspace();
  submitButtonDisabled: boolean = false;
  $v: any; //vuelidate
  public templates!: Template[] //Is assigned via mapstate
  public workspaces!: Dictionary<Workspace[]> //Is assigned via mapstate
  public sizeAndPrices!: SizeAndPrice[]; //Is assigned via mapstate
  public vmImages!: VmImage[]; //Is assigned via mapstate
  public vreConfiguration!: VreConfiguration; //Is assigned via mapstate
  public selectedSize: SizeAndPrice = null;
  public selectedTemplate: Template = null;
  public showVmSize: boolean = false;

  setDefaultSize(imageId: string, parameterName: string) {
    var selectedImage = this.vmImages.find(image => {
      return image.id === imageId;
    });
    this.selectedSize = this.sizeAndPrices.find(sizeAndPrice => {
      return sizeAndPrice.technicalName === selectedImage.defaultSizeTechnicalName;
    });
    if (!this.selectedSize) {
      console.error("Could not find size" + selectedImage.defaultSizeTechnicalName);
    }
    this.$v.createWorkspace.parameters[parameterName].value.$touch();
    this.createWorkspace.template = selectedImage.componentFile;
    this.sizeSelected(this.selectedSize);
  }

  sizeSelected(sizeAndPrice: SizeAndPrice) {
    this.selectedSize = sizeAndPrice;
    var param: any = Object.values(this.createWorkspace.parameters).find((param: any) => {
      return param.type === "vmSize"
    });
    param.value = sizeAndPrice.technicalName;
    this.showVmSize = false;
  }

  get budgetIncludingSurcharge() {
    return this.formatMoney((parseInt(this.createWorkspace.budget) * (this.vreConfiguration.surfToeslag / 100 + 1)) * 1.21);
  }

  formatMoney(money: number) {
    return Helper.formatMoney(money);
  }

  @Watch("templates")
  templatesChanged(value: Template[]) {
    var model = this;
    var selectedTemplate = value.length > 0 ? value[0] : null;
    this.createWorkspace.template = selectedTemplate ? selectedTemplate.filename : "";
    var createWorkspace = { ...this.createWorkspace };
    createWorkspace.parameters = {};
    if (selectedTemplate) {
      selectedTemplate.templateSpecificParameters.forEach(param => {
        createWorkspace.parameters[param.name] = new ComponentParameter_Post(param);
      });
      Vue.set(model, "createWorkspace", createWorkspace);
    }
    this.selectedTemplate = selectedTemplate;
  }


  validations() {
    var validations: any = {
      createWorkspace: {
        name: {
          required,
          alphaNum,
          minLength: minLength(4),
          maxLength: maxLength(44)
        },
        description: {
          required,
          maxLength: maxLength(1000)
        },
        template: {
          required
        },
        // owner:{
        //   required
        // },
        budget: {
          required,
          numeric,
          minValue: (value: string) => parseInt(value) > 0
        },
        endDate: {
          required,
          minValue: (value: Date) => value > new Date()
        },
        costcenter: {
          required: (value: string) => {
            //Old format
            if (value.length === 8 && !isNaN(Number(value))) {
              return true;
            }
            //new format
            var splitValue = value.split('-');
            if (splitValue.length === 2 && splitValue[0].length === 8 && splitValue[1].length === 2 && !isNaN(Number(splitValue[0])) && !isNaN(Number(splitValue[1]))) {
              return true;
            }
            return false;
          }
        },
        parameters: {}
      }
    };
    if (this.selectedTemplate && this.selectedTemplate.templateSpecificParameters) {
      this.selectedTemplate.templateSpecificParameters.forEach((parameter: TemplateParameter): void => {
        validations.createWorkspace.parameters[parameter.name] = {
          value: {
            required
          }
        }
      });
    }

    return validations;
  }

  async save() {
    this.$v.$touch();
    if (!this.$v.$invalid) {
      this.submitButtonDisabled = true;
      var createWorkspace = new CreateWorkspace(this.createWorkspace);
      var token = await this.$store.state.authModule.getapiTokenPopup();
      var result = await createWorkspace.create(token);
      if (result !== null) {
        this.$store.commit("setToaster", `You have succesfully requested workspace ${this.createWorkspace.name}. Your request is submitted to the budget holder and is pending approval.`);
        let key = `workspaces|${this.$store.state.authModule.userName}`;
        var workspaces = this.workspaces[key];
        var ws = new Workspace();
        ws.name = this.createWorkspace.name;
        ws.workspaceStatus = "Unavailable";
        ws.budgetStatus = "Requested";
        ws.budget = parseInt(this.createWorkspace.budget);
        ws.currentSpend = 0;
        workspaces.push(ws);
        var params = new AddWorkspacesParams(`workspaces|${this.$store.state.authModule.userName}`, workspaces);
        this.$store.commit('addWorkspaces', params);
        this.$router.push("/");
      } else {
        this.submitButtonDisabled = false;
      }
    }
  }
  @Watch("createWorkspace.owner")
  changeOwner(value: Date) {
    this.$v.createWorkspace.owner.$touch();
  }

  async isExternalUser() {
    const regex = /x\d{7}@/;
    const email = this.$store.state.authModule.userName;
    if (email.match(regex)) {
      this.submitButtonDisabled = true
    } else {
      this.submitButtonDisabled = false
    }
  }

  async created() {
    this.$store.dispatch("getTemplates");
    this.$store.dispatch("getSizeAndPrices");
    this.$store.dispatch("getVmImages");
    this.createWorkspace.endDate = new Date(new Date().getFullYear(), 11, 31, 23, 59);
    let getWorkspacesParams = new GetWorkspacesParams(this.$store.state.authModule.userName, false);
    this.$store.dispatch("getWorkspaces", getWorkspacesParams);
    this.templatesChanged(this.templates);
    this.isExternalUser();
  }
}
